export const APP_CONSTANTS = {
  APP_NAME: 'NiraxoBID',
  VERSION: '1.0.0',
  API_TIMEOUT: 30000,
} as const;

/**
 * localStorage schema, verbatim from mdfiles/loginapi.md section 5.
 *
 * The document puts the access token in localStorage alongside everything
 * else. That is a deliberate requirement, not an oversight on our side --
 * note that it makes the access token readable by any script on the origin and
 * survive a browser restart, which sessionStorage would not.
 */
export const STORAGE_KEYS = {
  USER_ID: 'userId',
  USER_NAME: 'userName',
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  EMAIL_ADDRESS: 'emailAddress',
  IS_AUTH: 'isAuth',
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  TOKEN_TYPE: 'tokenType',
  EXPIRES_IN: 'expiresIn',
  TOKEN_EXPIRY: 'tokenExpiry',
  /**
   * organization-setup-step1-apiflow.md section 2.1. Written at login even
   * when the API reports null, so "no organization yet" stays distinguishable
   * from "not checked yet" -- that distinction is what decides POST vs PUT on
   * Save & Next.
   */
  ORGANIZATION_CODE: 'organizationCode',
  /**
   * The legal entity the user is working in, which every request is scoped to.
   *
   * Remembered so a reload lands where they left off, and validated against the profile's assignments on
   * every sign-in — an entity no longer assigned is discarded rather than sent in a header that would
   * answer 403 on every screen.
   */
  SELECTED_ENTITY: 'selectedEntityId',
} as const;

/**
 * niraxobid.md section 4 lists 11 routes, but the sidebar menu in section 9.3
 * navigates to eight more. This is the superset -- every path the mock menu
 * emits has a route, so no menu item dead-ends.
 */
export const ROUTES = {
  // public
  LOGIN: '/login',
  /** The Supplier Bid Quotation portal's sign-in (2026-10-23): a one-time code to the supplier's registered e-mail. */
  SUPPLIER_LOGIN: '/supplier/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  UNAUTHORIZED: '/unauthorized',
  /* Where a tracked invitation link lands once it no longer leads anywhere. */
  INVITATION_INVALID: '/invitation-invalid',

  // shell
  ROOT: '/',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',

  // sidebar destinations
  RFQS: '/rfqs',
  /* The Active Templates picker and the New BID wizard; a record's own pages add its id. */
  RFQS_TEMPLATES: '/rfqs/templates',
  RFQS_NEW: '/rfqs/new',
  /* The supplier's side: the BIDs a supplier user is invited to; a bid adds the BID's id. */
  SUPPLIER_BIDS: '/supplier-bids',
  /* The Supplier Dashboard (2026-10-29, NB-637): the supplier's home -- KPI cards and the BID Calendar. */
  SUPPLIER_DASHBOARD: '/supplier-dashboard',
  PURCHASE_REQUESTS: '/purchase-requests',
  /*
    The sixth FUNCTION feature the RBAC matrix scores. It had no path, so a role granted it had nothing
    to be shown -- the matrix could grant a menu the application could not render.
  */
  REVERSE_AUCTION: '/reverse-auction',
  TEMPLATES: '/templates',
  TEMPLATES_PR: '/templates/pr',
  TEMPLATES_RFQ: '/templates/rfq',
  TEMPLATES_SUPPLIER_ONBOARDING: '/templates/supplier-onboarding',
  RBAC: '/rbac',
  WORKFLOW_CONFIG: '/workflow-config',
  AI_CONTROL: '/ai-control',
  MASTERS: '/masters',
  /*
    The Masters submenu, in the order all nine design bundles list it. Named
    constants rather than string building, because the sidebar resolves its
    active child by matching these against the route.
  */
  MASTERS_ORGANIZATION: '/masters/organization',
  MASTERS_ENTITY: '/masters/entity',
  MASTERS_BUSINESS_UNIT: '/masters/business-unit',
  MASTERS_DIVISION: '/masters/division',
  MASTERS_DEPARTMENT: '/masters/department',
  MASTERS_LOCATION: '/masters/location',
  MASTERS_CATEGORY: '/masters/category',
  MASTERS_UOM: '/masters/uom',
  MASTERS_COUNTRY: '/masters/country',
  MASTERS_STATE: '/masters/state',
  MASTERS_CITY: '/masters/city',
  MASTERS_MATERIAL: '/masters/material',
  MASTERS_CURRENCY: '/masters/currency',
  MASTERS_PAYMENT_TERMS: '/masters/payment-terms',
  MASTERS_DELIVERY_TERMS: '/masters/delivery-terms',
  MASTERS_INCOTERM: '/masters/incoterm',
  MASTERS_PR: '/masters/pr',
  MASTERS_SUPPLIER: '/masters/supplier',
  MASTERS_DESIGNATION: '/masters/designation',
  MASTERS_USER_MANAGEMENT: '/masters/user-management',

  ORGANIZATION_SETUP: '/organization-setup',
  USERS: '/users',
} as const;

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  /** A write refused by the state of the data, not by the request — a delete with associations, say. */
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
} as const;

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];
